import { parsePolygonScheduleDate } from './gantt-timeline.util';

const UNBOUNDED_FROM_ISO = '1970-01-01';

/** Platný vstup filtru Od–Do (`dd.mm.yyyy` nebo ISO datum). */
export function isValidDateFilterInput(value: string | null | undefined): boolean {
  if (!(value ?? '').trim()) return false;
  return parsePolygonScheduleDate(value) != null;
}

/** Obě hranice období jsou vyplněné a platné. */
export function isCompleteDateRangeFilter(
  from: string | null | undefined,
  to: string | null | undefined,
): boolean {
  return isValidDateFilterInput(from) && isValidDateFilterInput(to);
}

/** Alespoň jedna hranice (Od nebo Do) je platná. */
export function hasDateRangeFilter(
  from: string | null | undefined,
  to: string | null | undefined,
): boolean {
  return isValidDateFilterInput(from) || isValidDateFilterInput(to);
}

/** Obě hranice prázdné — načíst celou historii. */
export function isOpenEndedDateFilter(
  from: string | null | undefined,
  to: string | null | undefined,
): boolean {
  return !isValidDateFilterInput(from) && !isValidDateFilterInput(to);
}

/** Dnešní datum ve formátu `dd.mm.yyyy`. */
export function todayLocalCsDate(): string {
  return formatLocalCsDate(new Date());
}

/** První den aktuálního lokálního měsíce jako `dd.mm.yyyy`. */
export function startOfCurrentMonthCsDate(): string {
  const d = new Date();
  return `01.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
}

function formatLocalCsDate(d: Date): string {
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
}

/**
 * Aktuální celý kalendářní měsíc v lokálním čase (`dd.mm.yyyy`).
 * 19. 10. 2026 → Od 01.10.2026, Do 31.10.2026.
 */
export function currentFullCalendarMonthCsRange(now = new Date()): { from: string; to: string } {
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return { from: formatLocalCsDate(start), to: formatLocalCsDate(end) };
}

/**
 * Předchozí celý kalendářní měsíc v lokálním čase (`dd.mm.yyyy`).
 * 19. 10. 2026 → Od 01.09.2026, Do 30.09.2026. Ne klouzavých 30 dní, ne od začátku roku.
 */
export function previousFullCalendarMonthCsRange(now = new Date()): { from: string; to: string } {
  const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const end = new Date(now.getFullYear(), now.getMonth(), 0);
  return { from: formatLocalCsDate(start), to: formatLocalCsDate(end) };
}

/** Sentinel „od začátku“ (`1970-01-01` / `01.01.1970`) — není zvolené období. */
export function isEpochDateFilterInput(value: string | null | undefined): boolean {
  if (!isValidDateFilterInput(value)) return false;
  return toIsoDateOnly(value!) === UNBOUNDED_FROM_ISO;
}

/** Dnešní datum jako ISO `YYYY-MM-DD`. */
export function todayIsoDateOnly(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Převod `dd.mm.yyyy` nebo ISO na `YYYY-MM-DD`. */
export function toIsoDateOnly(value: string): string {
  const text = (value ?? '').trim();
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
  if (iso) return text;
  const cs = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/.exec(text);
  if (!cs) return text;
  return `${cs[3]}-${cs[2].padStart(2, '0')}-${cs[1].padStart(2, '0')}`;
}

/** Převod ISO `YYYY-MM-DD` na český formát `dd.mm.yyyy`; ostatní vstupy vrací otrimované. */
export function toCsDateOnly(value: string): string {
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec((value ?? '').trim());
  return iso ? `${iso[3]}.${iso[2]}.${iso[1]}` : (value ?? '').trim();
}

/** Lokální začátek dne pro datum filtr (`dd.mm.yyyy` nebo ISO). */
export function parseDateFilterInputToLocalDate(value: string | null | undefined): Date | null {
  const text = (value ?? '').trim();
  if (!text) return null;
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
  if (iso) return new Date(`${iso[1]}-${iso[2]}-${iso[3]}T00:00:00`);
  const cs = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/.exec(text);
  if (!cs) return null;
  return new Date(
    Number.parseInt(cs[3], 10),
    Number.parseInt(cs[2], 10) - 1,
    Number.parseInt(cs[1], 10),
    0,
    0,
    0,
    0,
  );
}

/** Inclusive range match against local calendar days. */
export function isDateWithinFilterRange(
  value: string | Date | null | undefined,
  from: string | null | undefined,
  to: string | null | undefined,
): boolean {
  const fromDate = parseDateFilterInputToLocalDate(from);
  const toDate = parseDateFilterInputToLocalDate(to);
  if (!fromDate && !toDate) return true;
  if (!value) return false;
  const target = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(target.getTime())) return false;
  if (fromDate && target.getTime() < fromDate.getTime()) return false;
  if (toDate) {
    const nextDayStart = new Date(toDate);
    nextDayStart.setDate(nextDayStart.getDate() + 1);
    if (target.getTime() >= nextDayStart.getTime()) return false;
  }
  return true;
}

/**
 * ISO rozsah pro API: prázdné Od → od začátku; prázdné Do → dnes;
 * obě prázdné → celá historie (1970–dnes).
 */
export function resolveDateRangeFilterIso(
  from: string | null | undefined,
  to: string | null | undefined,
): { from: string; to: string } {
  const fromValid = isValidDateFilterInput(from);
  const toValid = isValidDateFilterInput(to);
  if (!fromValid && !toValid) {
    return unboundedDateRangeIso();
  }
  return {
    from: fromValid ? toIsoDateOnly(from!) : UNBOUNDED_FROM_ISO,
    to: toValid ? toIsoDateOnly(to!) : todayIsoDateOnly(),
  };
}

/** Široké ISO období pro načtení všech záznamů (1970–dnes). */
export function unboundedDateRangeIso(): { from: string; to: string } {
  return { from: UNBOUNDED_FROM_ISO, to: todayIsoDateOnly() };
}

/**
 * ISO rozsah pro finance „skutečnost k datu“ — kumulativně od začátku do zadaného dne (nebo dnes).
 * Prázdný / neplatný vstup → konec období = dnes (stejně jako dříve výchozí pole Do).
 */
export function resolveFinanceAsOfDateIso(asOfDate: string | null | undefined): { dateFrom: string; dateTo: string } {
  const toValid = isValidDateFilterInput(asOfDate);
  return {
    dateFrom: UNBOUNDED_FROM_ISO,
    dateTo: toValid ? toIsoDateOnly(asOfDate!) : todayIsoDateOnly(),
  };
}
